import { useRecordContext, useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import type { ZoomMeetingSummaryView } from "../types";
import { isAwaitingZoom } from "./meetingAssets";

export const STATUS_CLASS: Record<string, string> = {
  pending:
    "border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-950 dark:text-orange-300",
  done: "border-green-300 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-950 dark:text-green-300",
};

// The meeting's review status and its outstanding drafts, merged into one badge.
// Reads straight off the zoom_meetings row (via the summary view): `summary_status`
// is `zoom_meetings.status` and `tasks_remaining` is derived from `zoom_meetings.tasks`.
//
// `status` wins over the count deliberately. "done" means a human finished
// reviewing the meeting — any drafts they left uncreated were a decision, not a
// backlog. Showing "4 left" on a meeting someone already closed out would nag
// forever about work they chose not to do.
//
// Shared by the Zoom Meetings list column and the dashboard "Recent Client Calls"
// widget so both read the same source and render identically.
export const ReviewStatusField = () => {
  const r = useRecordContext<ZoomMeetingSummaryView>();
  const translate = useTranslate();
  if (!r) return null;
  // A meeting that just ended has no review status yet and no files. Say it is
  // on its way rather than showing an empty cell (LCATOMICZO-76).
  if (!r.summary_status) {
    return isAwaitingZoom(r) ? (
      <Badge variant="outline" className="text-muted-foreground">
        {translate("resources.zoom_meetings.waiting_for_zoom")}
      </Badge>
    ) : null;
  }
  const done = r.summary_status === "done";
  return (
    <Badge variant="outline" className={cn(STATUS_CLASS[r.summary_status])}>
      {done
        ? translate("resources.zoom_meeting_summaries.status.done")
        : translate("resources.zoom_meetings.review.remaining", {
            smart_count: r.tasks_remaining ?? 0,
          })}
    </Badge>
  );
};
